'use client';
import { Carousel } from '@v2/components/Carousel/Carousel';
import { trackGa4ItemListView, trackGa4ItemSelection } from '@v2/analytics/ga4';
import { EventCardDTO } from '@v2/types';
import { Typography } from '@v2/ui/Typography';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef } from 'react';

interface Props {
  events: EventCardDTO[];
  title?: string;
  itemListName?: string;
  itemCategory?: string;
}

export const LeagueCarouselEvents = ({
  events,
  title,
  itemListName = 'event_carousel',
  itemCategory,
}: Props) => {
  const trackedListKeys = useRef(new Set<string>());
  const safeEvents = events ?? [];
  const itemListId = itemListName;
  const listKey = safeEvents
    .map((event) => `${event.id}:${event.title}`)
    .join('|');
  const trackingKey = `${itemListId}:${itemListName}:${itemCategory ?? ''}:${listKey}`;

  useEffect(() => {
    if (!safeEvents.length || trackedListKeys.current.has(trackingKey)) return;

    trackedListKeys.current.add(trackingKey);
    trackGa4ItemListView({
      itemListId,
      itemListName,
      items: safeEvents.map((event, index) => ({
        item_id: String(event.id),
        item_name: event.title,
        ...(itemCategory ? { item_category: itemCategory } : {}),
        index,
      })),
    });
  }, [itemCategory, itemListId, itemListName, safeEvents, trackingKey]);

  if (!safeEvents.length)
    return (
      <div className="flex h-[294px] flex-col items-center rounded-lg border border-gray-200">
        <div className="flex h-full flex-col justify-center text-center">
          <h2 className="mb-2 text-xl font-bold text-gray-800">
            No events found
          </h2>
          <p className="text-base font-semibold text-gray-500">
            Please try a different location
          </p>
        </div>
      </div>
    );

  return (
    <div>
      {title && <h2 className="mb-6 text-lg font-bold md:text-2xl">{title}</h2>}

      <Carousel>
        {safeEvents.map((event, index) => (
          <Link
            prefetch={false}
            target="_blank"
            href={event.href}
            key={event.id}
            onClick={() =>
              trackGa4ItemSelection({
                itemListId,
                itemListName,
                item: {
                  item_id: String(event.id),
                  item_name: event.title,
                  ...(itemCategory ? { item_category: itemCategory } : {}),
                  index,
                },
              })
            }
            className="flex flex-shrink-0 flex-grow-0 basis-72 flex-col"
          >
            <div className="flex w-[302px] flex-col">
              <div className="relative mb-3 h-40 w-full overflow-hidden rounded-lg">
                <Image
                  src={event.imageUrl}
                  alt={event.imageAlt}
                  fill
                  sizes="302px"
                  className="object-cover object-center"
                />
              </div>
              <div className="w-full max-w-64">
                <Typography
                  variant="h3"
                  size="tmd"
                  className="mb-2 font-semibold"
                >
                  {event.title}
                </Typography>
                <Typography size="tsm" className="font-medium text-gray-500">
                  {event.startDateLabel.startsWith('in ') ? (
                    <span className="font-semibold text-primary-500">
                      {event.startDateLabel}
                    </span>
                  ) : (
                    event.startDateLabel
                  )}{' '}
                  &middot; {event.startTimeLabel}
                  <br /> {event.locationLabel}
                </Typography>
              </div>
            </div>
          </Link>
        ))}
      </Carousel>
    </div>
  );
};
